import { Card, CardContent } from '@/components/ui/card';
import { useState, use, useEffect, useMemo, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AppDataContext } from '@/lib/app-data-hydration';
import { BIBLE_BOOKS } from '@/lib/utils/bible-utils';
import { OutlineDataContext } from '@/data/outline/outline-data-context';
import { getOutlinesByBook } from '@/data/outline/outline-data-service';
import { CultureByPassageDataContext } from '@/data/culture/culture-by-passage-data-context';
import type { BackgroundCounts, BackgroundPassageIds, BackgroundType, FilterStatus, ModelFilter } from './background/types';
import {
  isBookComplete, isBookEmpty, matchesModelFilter,
  calculateProPercentage
} from './background/utils';
import { AdminBackgroundStats } from './background/AdminBackgroundStats';
import { AdminBackgroundBookCard } from './background/AdminBackgroundBookCard';
import { useAdminVerification } from './AdminVerificationContext';

export default function AdminBackgroundData() {
  const { getIssuesForBook } = useAdminVerification();
  const [model, setModel] = useState<string>('gemini-3.1-pro-preview');
  const [regeneratingBooks, setRegeneratingBooks] = useState<Set<string>>(new Set());
  const [regeneratingOutlines, setRegeneratingOutlines] = useState<Set<string>>(new Set());
  const [generatingCulturalPrompts, setGeneratingCulturalPrompts] = useState<Set<string>>(new Set());
  const [generatingCulturalImages, setGeneratingCulturalImages] = useState<Set<string>>(new Set());
  const [generationProgress, setGenerationProgress] = useState<Map<string, string>>(new Map());
  const [backgroundCounts, setBackgroundCounts] = useState<Map<string, BackgroundCounts>>(new Map());
  const [backgroundPassageIds, setBackgroundPassageIds] = useState<Map<string, BackgroundPassageIds>>(new Map());
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [modelFilter, setModelFilter] = useState<ModelFilter>('all');
  const [expandedBooks, setExpandedBooks] = useState<Set<string>>(new Set());
  const [showCostDetails, setShowCostDetails] = useState(false);

  const { refresh } = use(AppDataContext);
  const { data: outlineData } = use(OutlineDataContext);
  const cultureByPassageContext = use(CultureByPassageDataContext);

  const outlinesByBook = outlineData ? getOutlinesByBook(outlineData.items) : new Map();

  const culturalImageStats = useMemo(() => {
    const statsMap = new Map<string, { total: number; completed: number; promptsCompleted: number; totalPassages: number }>();

    // Initialize stats for all books from backgroundCounts
    for (const book of BIBLE_BOOKS) {
      const counts = backgroundCounts.get(book.name);
      statsMap.set(book.name, {
        total: 0,
        completed: 0,
        promptsCompleted: 0,
        totalPassages: counts?.totalPassages || 0
      });
    }

    // Merge in stats from backgroundCounts (this is the primary source now)
    backgroundCounts.forEach((counts, bookName) => {
      if (counts.culturalImageStats) {
        statsMap.set(bookName, counts.culturalImageStats);
      }
    });

    return statsMap;
  }, [cultureByPassageContext, backgroundCounts]);

  const loadBackgroundCounts = useCallback(async (force = false) => {
    if (isLoadingCounts && !force) return;
    setIsLoadingCounts(true);
    try {
      const functions = getFunctions();
      const getCounts = httpsCallable(functions, 'getBackgroundDataCounts');
      const result: any = await getCounts();

      const countsMap = new Map<string, BackgroundCounts>();
      const passageIdsMap = new Map<string, BackgroundPassageIds>();

      if (result.data && result.data.books) {
        Object.entries(result.data.books).forEach(([book, data]: [string, any]) => {
          countsMap.set(book, {
            ...data,
            culturalPassageByModel: data.culturalPassageByModel || {},
            archaeologicalPassageByModel: data.archaeologicalPassageByModel || {},
            historicalPassageByModel: data.historicalPassageByModel || {},
            geographicByModel: data.geographicByModel || {},
            peoplePassageByModel: data.peoplePassageByModel || {},
            literaryPassageByModel: data.literaryPassageByModel || {},
            studyGuidePassageByModel: data.studyGuidePassageByModel || {},
            guidedStudyPassageByModel: data.guidedStudyPassageByModel || {},
          });

          if (data.passageIds) {
            passageIdsMap.set(book, data.passageIds);
          }
        });
      }

      setBackgroundCounts(countsMap);
      setBackgroundPassageIds(passageIdsMap);
    } catch (error) {
      console.error('Failed to load background counts:', error);
    } finally {
      setIsLoadingCounts(false);
    }
  }, [isLoadingCounts]);

  useEffect(() => {
    loadBackgroundCounts();
  }, []);

  const getRegeneratingKey = (book: string, type: BackgroundType) => `${book}:${type}`;

  const handleGenerateBackground = async (book: string, type: BackgroundType, onlyMissing = false, overrideModel?: string) => {
    const key = getRegeneratingKey(book, type);
    setRegeneratingBooks(prev => new Set(prev).add(key));

    const functions = getFunctions();
    let functionName = '';
    switch (type) {
      case 'culturalBook': functionName = 'generateCultureByBook'; break;
      case 'culturalPassage': functionName = 'generateCultureByPassageForBook'; break;
      case 'archaeologicalBook': functionName = 'generateArchaeologyByBook'; break;
      case 'archaeologicalPassage': functionName = 'generateArchaeologyByPassageForBook'; break;
      case 'historicalBook': functionName = 'generateHistoricalEventsByBook'; break;
      case 'historicalPassage': functionName = 'generateHistoricalEventsByPassageForBook'; break;
      case 'geographic': functionName = 'generatePlacesByPassageForBook'; break;
      case 'peoplePassage': functionName = 'generatePeopleByPassageForBook'; break;
      case 'literaryBook': functionName = 'literaryFeatureGeneration'; break;
      case 'literaryPassage': functionName = 'generateLiteraryFeaturesByPassageForBook'; break;
      case 'studyGuidePassage': functionName = 'generateStudyGuideByPassageForBook'; break;
      case 'guidedStudyPassage': functionName = 'generateGuidedStudyByPassageForBook'; break;
    }

    try {
      const generateFunction = httpsCallable(functions, functionName, { timeout: 600000 });
      let passageIdsToGenerate: string[] | undefined = undefined;

      if (onlyMissing) {
        const bookPassageIds = backgroundPassageIds.get(book);
        if (bookPassageIds) {
          const allIds = new Set(bookPassageIds.allPassageIds);
          let existingIds: Set<string>;
          switch (type) {
            case 'culturalPassage': existingIds = new Set(bookPassageIds.culturalPassageIds); break;
            case 'archaeologicalPassage': existingIds = new Set(bookPassageIds.archaeologicalPassageIds); break;
            case 'historicalPassage': existingIds = new Set(bookPassageIds.historicalPassageIds); break;
            case 'geographic': existingIds = new Set(bookPassageIds.geographicIds); break;
            case 'peoplePassage': existingIds = new Set(bookPassageIds.peoplePassageIds); break;
            case 'literaryPassage': existingIds = new Set(bookPassageIds.literaryPassageIds); break;
            case 'studyGuidePassage': existingIds = new Set(bookPassageIds.studyGuidePassageIds); break;
            case 'guidedStudyPassage': existingIds = new Set(bookPassageIds.guidedStudyPassageIds); break;
            default: existingIds = new Set();
          }
          passageIdsToGenerate = Array.from(allIds).filter(id => !existingIds.has(id));
        }
      } else if (overrideModel && !type.endsWith('Book')) {
        // This is an "Upgrade" call - target both non-pro AND missing passages to ensure whole book is Pro
        const bookPassageIds = backgroundPassageIds.get(book);
        if (bookPassageIds && bookPassageIds.nonProPassageIds) {
          const allIds = new Set(bookPassageIds.allPassageIds);
          let existingIds: Set<string>;
          let nonProIds: string[];

          switch (type) {
            case 'culturalPassage':
              existingIds = new Set(bookPassageIds.culturalPassageIds);
              nonProIds = bookPassageIds.nonProPassageIds.culturalPassageIds;
              break;
            case 'archaeologicalPassage':
              existingIds = new Set(bookPassageIds.archaeologicalPassageIds);
              nonProIds = bookPassageIds.nonProPassageIds.archaeologicalPassageIds;
              break;
            case 'historicalPassage':
              existingIds = new Set(bookPassageIds.historicalPassageIds);
              nonProIds = bookPassageIds.nonProPassageIds.historicalPassageIds;
              break;
            case 'geographic':
              existingIds = new Set(bookPassageIds.geographicIds);
              nonProIds = bookPassageIds.nonProPassageIds.geographicIds;
              break;
            case 'peoplePassage':
              existingIds = new Set(bookPassageIds.peoplePassageIds);
              nonProIds = bookPassageIds.nonProPassageIds.peoplePassageIds;
              break;
            case 'literaryPassage':
              existingIds = new Set(bookPassageIds.literaryPassageIds);
              nonProIds = bookPassageIds.nonProPassageIds.literaryPassageIds;
              break;
            case 'studyGuidePassage':
              existingIds = new Set(bookPassageIds.studyGuidePassageIds);
              nonProIds = bookPassageIds.nonProPassageIds.studyGuidePassageIds;
              break;
            case 'guidedStudyPassage':
              existingIds = new Set(bookPassageIds.guidedStudyPassageIds);
              nonProIds = bookPassageIds.nonProPassageIds.guidedStudyPassageIds;
              break;
            default:
              existingIds = new Set();
              nonProIds = [];
          }

          const missingIds = Array.from(allIds).filter(id => !existingIds.has(id));
          passageIdsToGenerate = [...new Set([...nonProIds, ...missingIds])];
        }
      }

      if (passageIdsToGenerate && passageIdsToGenerate.length === 0) {
        // Skip if there's nothing to upgrade
        return;
      }

      const modelToUse = overrideModel || model;

      // If we have a lot of passages and we're using a slow Pro model, 
      // chunk them on the frontend so each function call has its own 9-minute timeout.
      const MAX_PASSAGES_PER_CALL = 20;
      const passageChunks: string[][] = [];

      if (passageIdsToGenerate && passageIdsToGenerate.length > MAX_PASSAGES_PER_CALL) {
        for (let i = 0; i < passageIdsToGenerate.length; i += MAX_PASSAGES_PER_CALL) {
          passageChunks.push(passageIdsToGenerate.slice(i, i + MAX_PASSAGES_PER_CALL));
        }
      } else if (passageIdsToGenerate) {
        passageChunks.push(passageIdsToGenerate);
      } else {
        passageChunks.push([]); // Handle "all passages" case (not chunked)
      }

      console.log(`Triggering ${passageChunks.length} parallel cloud function calls for ${type}...`);

      const callPromises = passageChunks.map(async (chunk) => {
        const params: any = {
          book,
          model: modelToUse,
          batchSize: 20 // Full parallel within the chunk
        };
        if (chunk.length > 0) {
          params.passageIds = chunk;
        }
        return generateFunction(params);
      });

      await Promise.all(callPromises);
      await loadBackgroundCounts(true);
      await refresh();
    } catch (error) {
      console.error(`Failed to generate ${type} for ${book}:`, error);
      alert(`Failed to generate ${type} for ${book}. Check console for details.`);
    } finally {
      setRegeneratingBooks(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleBulkUpgrade = async (book: string) => {
    const bookPassageIds = backgroundPassageIds.get(book);
    if (!bookPassageIds || !bookPassageIds.nonProPassageIds) return;

    const datasets: BackgroundType[] = [
      'culturalPassage',
      'archaeologicalPassage',
      'historicalPassage',
      'literaryPassage',
      'peoplePassage',
      'geographic',
      'studyGuidePassage'
    ];

    const upgradeModel = 'gemini-3.1-pro-preview';

    const upgradePromises = datasets.map(async (type) => {
      let count = 0;
      const counts = backgroundCounts.get(book);
      const passageCount = (counts as any)[type] || 0;
      const missingCount = (counts?.totalPassages || 0) - passageCount;

      const nonPro = bookPassageIds.nonProPassageIds;
      let nonProCount = 0;
      switch (type) {
        case 'culturalPassage': nonProCount = nonPro.culturalPassageIds.length; break;
        case 'archaeologicalPassage': nonProCount = nonPro.archaeologicalPassageIds.length; break;
        case 'historicalPassage': nonProCount = nonPro.historicalPassageIds.length; break;
        case 'literaryPassage': nonProCount = nonPro.literaryPassageIds.length; break;
        case 'peoplePassage': nonProCount = nonPro.peoplePassageIds.length; break;
        case 'geographic': nonProCount = nonPro.geographicIds.length; break;
        case 'studyGuidePassage': nonProCount = nonPro.studyGuidePassageIds.length; break;
      }
      count = nonProCount + missingCount;

      if (count > 0) {
        console.log(`Bulk Upgrade: Starting ${type} for ${book} (${count} passages)`);
        return handleGenerateBackground(book, type, false, upgradeModel);
      }
    });

    await Promise.all(upgradePromises);
  };

  const handleGenerateOutline = async (book: string) => {
    setRegeneratingOutlines(prev => new Set(prev).add(book));
    const functions = getFunctions();
    const outlineGeneration = httpsCallable(functions, 'outlineGeneration', { timeout: 600000 });
    try {
      await outlineGeneration({ book, model });
      await refresh();
    } catch (error) {
      console.error(`Failed to generate outline for ${book}:`, error);
      alert(`Failed to generate outline for ${book}. Check console for details.`);
    } finally {
      setRegeneratingOutlines(prev => {
        const next = new Set(prev);
        next.delete(book);
        return next;
      });
    }
  };

  const handleGenerateCulturalPrompts = async (book: string) => {
    setGeneratingCulturalPrompts(prev => new Set(prev).add(book));
    setGenerationProgress(prev => new Map(prev).set(book, 'Starting prompt generation...'));
    try {
      let passageIds: string[] = [];

      // Try to use backgroundPassageIds first (from Cloud Function counts)
      const bookPassageIds = backgroundPassageIds.get(book);
      if (bookPassageIds && bookPassageIds.culturalPassageIds.length > 0) {
        passageIds = bookPassageIds.culturalPassageIds;
      } else if (cultureByPassageContext) {
        // Fallback to local context if available
        passageIds = cultureByPassageContext.cultureByPassage
          .filter(c => c.book === book && c.keyPoints && c.keyPoints.length > 0)
          .map(c => c.passageId);
      }

      if (passageIds.length === 0) {
        console.warn(`No passages with cultural data found for ${book}`);
        return;
      }

      const functions = getFunctions();
      const generatePrompts = httpsCallable(functions, 'generateCulturalPointImagePromptsBatch', { timeout: 600000 });
      const batchSize = 20;
      for (let i = 0; i < passageIds.length; i += batchSize) {
        const chunk = passageIds.slice(i, i + batchSize);
        setGenerationProgress(prev => new Map(prev).set(book, `Generating prompts... Batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(passageIds.length / batchSize)}`));
        await generatePrompts({
          passageIds: chunk,
          modelName: 'gemini-3-flash-preview'
        });
      }
      await refresh();
    } catch (error) {
      console.error(`Failed to generate cultural prompts for ${book}:`, error);
    } finally {
      setGeneratingCulturalPrompts(prev => {
        const next = new Set(prev);
        next.delete(book);
        return next;
      });
      setGenerationProgress(prev => {
        const next = new Map(prev);
        next.delete(book);
        return next;
      });
    }
  };

  const handleGenerateCulturalImages = async (book: string) => {
    setGeneratingCulturalImages(prev => new Set(prev).add(book));
    setGenerationProgress(prev => new Map(prev).set(book, 'Starting image generation...'));
    try {
      let passageIds: string[] = [];

      // Use backgroundPassageIds from counts
      const bookPassageIds = backgroundPassageIds.get(book);
      if (bookPassageIds && bookPassageIds.culturalPassageIds.length > 0) {
        passageIds = bookPassageIds.culturalPassageIds;
      } else if (cultureByPassageContext) {
        passageIds = cultureByPassageContext.cultureByPassage
          .filter(c => c.book === book && c.keyPoints && c.keyPoints.length > 0)
          .map(c => c.passageId);
      }

      if (passageIds.length === 0) {
        console.warn(`No passages with cultural data found for ${book}`);
        return;
      }

      const functions = getFunctions();
      const generateImages = httpsCallable(functions, 'generateCulturalPointImagesBatch', { timeout: 600000 });
      const batchSize = 5;
      for (let i = 0; i < passageIds.length; i += batchSize) {
        const chunk = passageIds.slice(i, i + batchSize);
        setGenerationProgress(prev => new Map(prev).set(book, `Generating images... Batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(passageIds.length / batchSize)}`));
        await generateImages({ passageIds: chunk });
      }
      await refresh();
    } catch (error) {
      console.error(`Failed to generate cultural images for ${book}:`, error);
    } finally {
      setGeneratingCulturalImages(prev => {
        const next = new Set(prev);
        next.delete(book);
        return next;
      });
      setGenerationProgress(prev => {
        const next = new Map(prev);
        next.delete(book);
        return next;
      });
    }
  };

  const toggleBookExpanded = (book: string) => {
    setExpandedBooks(prev => {
      const next = new Set(prev);
      if (next.has(book)) next.delete(book);
      else next.add(book);
      return next;
    });
  };

  const filteredBooks = useMemo(() => {
    return BIBLE_BOOKS.filter(book => {
      const counts = backgroundCounts.get(book.name);
      if (searchQuery && !book.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (counts && filterStatus !== 'all') {
        if (filterStatus === 'complete' && !isBookComplete(counts)) return false;
        if (filterStatus === 'empty' && !isBookEmpty(counts)) return false;
        if (filterStatus === 'partial' && (isBookComplete(counts) || isBookEmpty(counts))) return false;
      }
      if (counts && modelFilter !== 'all') {
        if (!matchesModelFilter(counts, modelFilter)) return false;
      }
      return true;
    });
  }, [searchQuery, filterStatus, modelFilter, backgroundCounts]);

  const aggregateStats = useMemo(() => {
    let completeBooks = 0, partialBooks = 0, emptyBooks = 0, totalProPct = 0;
    BIBLE_BOOKS.forEach(book => {
      const counts = backgroundCounts.get(book.name);
      if (!counts) {
        emptyBooks++;
        return;
      }
      if (isBookComplete(counts)) completeBooks++;
      else if (isBookEmpty(counts)) emptyBooks++;
      else partialBooks++;

      totalProPct += calculateProPercentage(counts);
    });
    return {
      totalBooks: BIBLE_BOOKS.length,
      completeBooks,
      partialBooks,
      emptyBooks,
      overallCompletion: BIBLE_BOOKS.length > 0 ? (completeBooks / BIBLE_BOOKS.length) * 100 : 0,
      overallProPercentage: BIBLE_BOOKS.length > 0 ? totalProPct / BIBLE_BOOKS.length : 0
    };
  }, [backgroundCounts]);

  return (
    <Card>
      <AdminBackgroundStats
        aggregateStats={aggregateStats}
        model={model}
        setModel={setModel}
        showCostDetails={showCostDetails}
        setShowCostDetails={setShowCostDetails}
        isLoadingCounts={isLoadingCounts}
        onRefreshCounts={loadBackgroundCounts}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        modelFilter={modelFilter}
        setModelFilter={setModelFilter}
      />

      <CardContent className="space-y-2">
        {filteredBooks.map(book => {
          const counts = backgroundCounts.get(book.name) || {
            culturalPassage: 0, culturalBook: 0, archaeologicalPassage: 0, archaeologicalBook: 0,
            historicalPassage: 0, historicalBook: 0, geographic: 0, peoplePassage: 0,
            literaryPassage: 0, literaryBook: 0, studyGuidePassage: 0, guidedStudyPassage: 0, totalPassages: 0
          };
          const hasOutline = (outlinesByBook.get(book.name) || []).length > 0;
          const imgStats = culturalImageStats.get(book.name) || { total: 0, completed: 0, promptsCompleted: 0, totalPassages: 0 };

          return (
            <AdminBackgroundBookCard
              key={book.name}
              book={book.name}
              counts={counts}
              currentModel={model}
              hasOutline={hasOutline}
              isExpanded={expandedBooks.has(book.name)}
              onToggleExpand={toggleBookExpanded}
              onGenerate={handleGenerateBackground}
              onGenerateOutline={handleGenerateOutline}
              onGenerateCulturalPrompts={handleGenerateCulturalPrompts}
              onGenerateCulturalImages={handleGenerateCulturalImages}
              onBulkUpgrade={handleBulkUpgrade}

              isCulturalBookGenerating={regeneratingBooks.has(getRegeneratingKey(book.name, 'culturalBook'))}
              isCulturalPassageGenerating={regeneratingBooks.has(getRegeneratingKey(book.name, 'culturalPassage'))}
              isArchBookGenerating={regeneratingBooks.has(getRegeneratingKey(book.name, 'archaeologicalBook'))}
              isArchPassageGenerating={regeneratingBooks.has(getRegeneratingKey(book.name, 'archaeologicalPassage'))}
              isHistBookGenerating={regeneratingBooks.has(getRegeneratingKey(book.name, 'historicalBook'))}
              isHistPassageGenerating={regeneratingBooks.has(getRegeneratingKey(book.name, 'historicalPassage'))}
              isGeoGenerating={regeneratingBooks.has(getRegeneratingKey(book.name, 'geographic'))}
              isPeoplePassageGenerating={regeneratingBooks.has(getRegeneratingKey(book.name, 'peoplePassage'))}
              isLiteraryPassageGenerating={regeneratingBooks.has(getRegeneratingKey(book.name, 'literaryPassage'))}
              isLiteraryBookGenerating={regeneratingBooks.has(getRegeneratingKey(book.name, 'literaryBook'))}
              isOutlineGenerating={regeneratingOutlines.has(book.name)}
              isPromptsGenerating={generatingCulturalPrompts.has(book.name)}
              isImagesGenerating={generatingCulturalImages.has(book.name)}
              isStudyGuideGenerating={regeneratingBooks.has(getRegeneratingKey(book.name, 'studyGuidePassage'))}
              isGuidedStudyGenerating={regeneratingBooks.has(getRegeneratingKey(book.name, 'guidedStudyPassage'))}

              imgTotal={imgStats.total}
              imgCompleted={imgStats.completed}
              imgPromptsCompleted={imgStats.promptsCompleted}
              imgTotalPassages={imgStats.totalPassages}

              generationProgress={generationProgress.get(book.name)}
              verificationIssues={getIssuesForBook(book.name)}
            />
          );
        })}
      </CardContent>
    </Card>
  );
}
